import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownContentMultiselect,
} from 'equal-ds-ui';
import { Loader2 } from 'lucide-react';
import { adminUsersAPI } from '../../../services/admin-users-api.service';
import { purposeAPI } from '../../../services/purpose-api.service';
import { centralRegistryAPI } from '../../../services/central-registry-api.service';
import type { AdminRole } from '../../../types/admin-users';
import type { PurposeRecord } from '../../../types/purpose';
import type { AdminUserFormValues, AdminUser } from '../../../types/admin-users';

export type AdminUserFormMode = 'create' | 'edit';

export interface AdminUserFormDrawerProps {
  isOpen: boolean;
  mode: AdminUserFormMode;
  initialValues?: Partial<AdminUserFormValues>;
  editingUser?: AdminUser | null;
  onClose: () => void;
  onSubmit: (values: AdminUserFormValues) => Promise<void> | void;
  isSubmitting?: boolean;
}

interface FormErrors {
  [key: string]: string | undefined;
}

const DEFAULT_VALUES: AdminUserFormValues = {
  user_role_id: '',
  name: '',
  mobile: '',
  email: '',
  emp_id: '',
  division: '',
  dept_owner: '',
  dept_code: '',
  demand_num: '',
  email_business_owner: '',
  purpose: [],
  channel_type: [],
  password: '',
};

function deriveInitialValues(
  editingUser?: AdminUser | null,
  initialValues?: Partial<AdminUserFormValues>
): AdminUserFormValues {
  if (editingUser) {
    const profile = editingUser.user_profile ?? ({} as AdminUser['user_profile']);

    const derivedPurpose = Array.isArray(profile.purpose)
      ? profile.purpose.map((value) => String(value))
      : [];
    const derivedChannelType = Array.isArray(profile.channel_type)
      ? profile.channel_type.map((value) => String(value))
      : [];

    return {
      user_role_id: String(profile.roles?.id ?? ''),
      name: profile.name ?? '',
      mobile: profile.mobile ?? '',
      email: editingUser.email ?? '',
      emp_id: String(profile.emp_id ?? ''),
      division: profile.division ?? '',
      dept_owner: profile.dept_owner ?? '',
      dept_code: profile.dept_code ?? '',
      demand_num: profile.demand_num ?? '',
      email_business_owner: profile.email_business_owner ?? '',
      purpose: derivedPurpose,
      channel_type: derivedChannelType,
      password: '',
      ...initialValues,
    };
  }

  return {
    ...DEFAULT_VALUES,
    ...initialValues,
  };
}

/**
 * Role-based validation logic (matching Angular implementation):
 * - Role 1 (Admin): No additional fields required
 * - Role 2: Division, Dept Code, Dept Owner, Business Owner Email required
 * - Other roles (Loan Officer, etc.): ALL fields required including Demand No, Purpose, Channel Type
 */

// Returns true if role requires Division, Dept Code, Dept Owner, Business Owner Email
function isFormRequired(roleId: string): boolean {
  return roleId !== '1' && roleId !== '';
}

// Returns true if role requires Demand No, Purpose, Channel Type (Loan Officer and similar roles)
function isLoanOfficerRole(roleId: string): boolean {
  return roleId !== '1' && roleId !== '2' && roleId !== '';
}

// Validation patterns matching Angular implementation exactly
const mobileRegex = /^[6-9]\d{9}$/;
const nameRegex = /^(?=.*[a-zA-Z ])[a-zA-Z ]+$/; // Letters and spaces only (matching Angular)
const empIdRegex = /^[a-zA-Z0-9]+$/;
const emailRegex = /^[a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,20}$/; // Lowercase only (matching Angular)
// Password: min 8 chars, uppercase, lowercase, number, special char (matching Angular)
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@$!%*?&])(?!.*\s).{8,}$/;
// Additional field validations (matching Angular's customFieldValidation)
const alphaNumRegex = /^[a-zA-Z0-9]+$/; // For dept_code, demand_num
const alphaRegex = /^[a-zA-Z]+[a-zA-Z ]+$/; // For division, dept_owner (letters, then letters+spaces)

/**
 * Shared validation helper to reduce duplication between validateField and validate.
 * Returns an error message if validation fails, undefined if valid.
 */
interface ValidationContext {
  roleId: string;
  isCreate: boolean;
}

function getFieldError(
  key: keyof AdminUserFormValues,
  value: string | string[] | undefined,
  context: ValidationContext
): string | undefined {
  const formRequired = isFormRequired(context.roleId);
  const loanOfficer = isLoanOfficerRole(context.roleId);
  const strValue = typeof value === 'string' ? value : '';

  switch (key) {
    case 'user_role_id':
      if (!strValue) return 'Please select a role';
      return undefined;

    case 'name':
      if (!strValue || !nameRegex.test(strValue)) return 'Please enter valid name.';
      return undefined;

    case 'mobile':
      if (!strValue || !mobileRegex.test(strValue)) return 'Please enter valid mobile number.';
      return undefined;

    case 'email':
      if (!strValue || !emailRegex.test(strValue.toLowerCase())) return 'Please enter valid email id.';
      if (!isValidDomainEmail(strValue.toLowerCase())) return getWhitelistingErrorMessage();
      return undefined;

    case 'emp_id':
      if (!strValue || !empIdRegex.test(strValue)) return 'Please enter valid employee id.';
      return undefined;

    case 'password':
      if (context.isCreate) {
        if (!strValue) return 'Please enter valid password.';
        if (!passwordRegex.test(strValue)) {
          return 'Password must contain atleast 8 characters (An uppercase letter, a lowercase letter, a number and a special character).';
        }
      }
      return undefined;

    case 'division':
      if (strValue && !alphaRegex.test(strValue)) return 'Please enter valid Department/Division name.';
      if (formRequired && !strValue) return 'Please enter valid Department/Division name.';
      return undefined;

    case 'dept_code':
      if (strValue && !alphaNumRegex.test(strValue)) return 'Please enter valid Business Department Code.';
      if (formRequired && !strValue) return 'Please enter valid Business Department Code.';
      return undefined;

    case 'dept_owner':
      if (strValue && !alphaRegex.test(strValue)) return 'Please enter valid Business Department Owner.';
      if (formRequired && !strValue) return 'Please enter valid Business Department Owner.';
      return undefined;

    case 'demand_num':
      if (strValue && !alphaNumRegex.test(strValue)) return 'Please enter valid Demand No.';
      if (formRequired && loanOfficer && !strValue) return 'Please enter valid Demand No.';
      return undefined;

    case 'email_business_owner':
      if (strValue && !emailRegex.test(strValue.toLowerCase())) return 'Please enter valid Email Id of Business Owner.';
      if (formRequired && !strValue) return 'Please enter valid Email Id of Business Owner.';
      return undefined;

    case 'purpose':
      if (formRequired && loanOfficer && (!Array.isArray(value) || value.length === 0)) {
        return 'Please enter valid Purpose Details.';
      }
      return undefined;

    case 'channel_type':
      if (formRequired && loanOfficer && (!Array.isArray(value) || value.length === 0)) {
        return 'Please enter valid Channel Type.';
      }
      return undefined;

    default:
      return undefined;
  }
}

/**
 * Email whitelisting check (matching Angular implementation)
 * Checks sessionStorage for:
 * - emailValidation: flag to enable/disable whitelisting
 * - emailwhitelisting: comma-separated allowed domains
 * - emailwhitelistingError: custom error message
 */
function isEmailWhitelistingEnabled(): boolean {
  return sessionStorage.getItem('emailValidation') === 'true';
}

function getWhitelistedDomains(): string[] {
  const domains = sessionStorage.getItem('emailwhitelisting');
  return domains ? domains.split(',').map((d) => d.trim().toLowerCase()) : [];
}

function getWhitelistingErrorMessage(): string {
  return sessionStorage.getItem('emailwhitelistingError') || 'Email domain is not allowed.';
}

function isValidDomainEmail(email: string): boolean {
  if (!isEmailWhitelistingEnabled()) {
    return true; // No whitelisting, all domains allowed
  }

  const emailDomain = email.split('@')[1]?.toLowerCase();
  if (!emailDomain) {
    return false;
  }

  const whitelistedDomains = getWhitelistedDomains();
  if (whitelistedDomains.length === 0) {
    return true; // No domains configured, all allowed
  }

  return whitelistedDomains.includes(emailDomain);
}

export const AdminUserFormDrawer: React.FC<AdminUserFormDrawerProps> = ({
  isOpen,
  mode,
  initialValues,
  editingUser,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [formValues, setFormValues] = useState<AdminUserFormValues>(() =>
    deriveInitialValues(editingUser, initialValues)
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [purposeOptions, setPurposeOptions] = useState<PurposeRecord[]>([]);
  const [channelTypes, setChannelTypes] = useState<string[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  // Key existence check state (for duplicate validation)
  const [existenceErrors, setExistenceErrors] = useState<{
    email: boolean;
    mobile: boolean;
    emp_id: boolean;
  }>({ email: false, mobile: false, emp_id: false });

  // Email whitelisting error state
  const [emailWhitelistError, setEmailWhitelistError] = useState<string | null>(null);

  // Store original values for edit mode comparison
  const [originalValues, setOriginalValues] = useState<{
    mobile: string;
    emp_id: string;
  }>({ mobile: '', emp_id: '' });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const derived = deriveInitialValues(editingUser, initialValues);
    setFormValues(derived);
    // Reset errors when form opens
    setErrors({});
    setExistenceErrors({ email: false, mobile: false, emp_id: false });
    setEmailWhitelistError(null);
    // Store original values for edit mode comparison
    if (editingUser) {
      setOriginalValues({
        mobile: derived.mobile,
        emp_id: derived.emp_id,
      });
    } else {
      setOriginalValues({ mobile: '', emp_id: '' });
    }
  }, [isOpen, editingUser, initialValues]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    async function fetchOptions() {
      try {
        setIsLoadingOptions(true);
        const [rolesResponse, purposeResponse, channelResponse] =
          await Promise.all([
            adminUsersAPI.getRoles(),
            purposeAPI.getPurposes({ limit: 200, pageno: 1 }),
            centralRegistryAPI.getChannelTypes(),
          ]);

        if (cancelled) {
          return;
        }

        setRoles(rolesResponse);
        setPurposeOptions(purposeResponse?.data?.rows ?? []);
        setChannelTypes(channelResponse ?? []);

        if (!editingUser && !initialValues && rolesResponse.length > 0) {
          setFormValues((prev) => ({
            ...prev,
            user_role_id: String(rolesResponse[0].id),
          }));
        }
      } catch (error) {
        console.error('[AdminUserFormDrawer] Failed to load options', error);
      } finally {
        if (!cancelled) {
          setIsLoadingOptions(false);
        }
      }
    }

    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [isOpen, editingUser, initialValues]);

  const handleInputChange = useCallback(
    (key: keyof AdminUserFormValues, value: string) => {
      // Auto-lowercase email fields (matching Angular behavior)
      const processedValue = (key === 'email' || key === 'email_business_owner')
        ? value.toLowerCase()
        : value;

      setFormValues((prev) => ({
        ...prev,
        [key]: processedValue,
      }));
    },
    []
  );

  /**
   * Validate a single field on blur (real-time validation matching Angular)
   * Uses shared getFieldError helper for consistent validation logic
   */
  const validateField = useCallback(
    (key: keyof AdminUserFormValues, value: string) => {
      const context: ValidationContext = {
        roleId: formValues.user_role_id,
        isCreate: mode === 'create',
      };

      // Handle email whitelisting separately (needs to update separate state)
      if (key === 'email') {
        if (!value || !emailRegex.test(value.toLowerCase())) {
          setErrors((prev) => ({ ...prev, email: 'Please enter valid email id.' }));
          setEmailWhitelistError(null);
        } else if (!isValidDomainEmail(value.toLowerCase())) {
          setErrors((prev) => {
            const next = { ...prev };
            delete next.email;
            return next;
          });
          setEmailWhitelistError(getWhitelistingErrorMessage());
        } else {
          setErrors((prev) => {
            const next = { ...prev };
            delete next.email;
            return next;
          });
          setEmailWhitelistError(null);
        }
        return;
      }

      // Use shared helper for other fields
      const error = getFieldError(key, value, context);

      setErrors((prev) => {
        const nextErrors = { ...prev };
        if (error) {
          nextErrors[key] = error;
        } else {
          delete nextErrors[key];
        }
        return nextErrors;
      });
    },
    [formValues.user_role_id, mode]
  );

  /**
   * Check if a key (email/mobile/emp_id) already exists in the system
   * Called on blur of the respective fields
   */
  const checkKeyExistence = useCallback(
    async (keyType: 'email' | 'mobile' | 'emp_id', value: unknown) => {
      // Skip if value is empty, not a string, or invalid
      if (!value || typeof value !== 'string' || value.trim() === '') {
        setExistenceErrors((prev) => ({ ...prev, [keyType]: false }));
        return;
      }

      // For email, convert to lowercase
      const keyValue = keyType === 'email' ? value.toLowerCase() : value;

      // Validate format before checking
      if (keyType === 'email' && !emailRegex.test(keyValue)) {
        return;
      }
      if (keyType === 'mobile' && !mobileRegex.test(keyValue)) {
        return;
      }
      if (keyType === 'emp_id' && !empIdRegex.test(keyValue)) {
        return;
      }

      // In edit mode, skip check if value hasn't changed from original
      if (mode === 'edit') {
        if (keyType === 'mobile' && keyValue === originalValues.mobile) {
          setExistenceErrors((prev) => ({ ...prev, mobile: false }));
          return;
        }
        if (keyType === 'emp_id' && keyValue === originalValues.emp_id) {
          setExistenceErrors((prev) => ({ ...prev, emp_id: false }));
          return;
        }
        // In edit mode, don't check email (email can't be changed)
        if (keyType === 'email') {
          return;
        }
      }

      try {
        const response = await adminUsersAPI.checkKeyExistence({
          keyType,
          keyValue,
        });

        const exists = response.message === 'key exist';
        setExistenceErrors((prev) => ({ ...prev, [keyType]: exists }));
      } catch (error) {
        console.error(`[AdminUserFormDrawer] Error checking ${keyType} existence:`, error);
      }
    },
    [mode, originalValues]
  );

  const handleMultiSelectChange = useCallback(
    (key: 'purpose' | 'channel_type', selected: string[]) => {
      setFormValues((prev) => ({
        ...prev,
        [key]: selected,
      }));
    },
    []
  );

  /**
   * Validate all form fields on submit.
   * Uses shared getFieldError helper for consistent validation logic.
   */
  const validate = useCallback(
    (values: AdminUserFormValues, isCreateMode: boolean): FormErrors => {
      const context: ValidationContext = {
        roleId: values.user_role_id,
        isCreate: isCreateMode,
      };

      const nextErrors: FormErrors = {};

      // Validate all fields using the shared helper
      const fieldsToValidate: (keyof AdminUserFormValues)[] = [
        'user_role_id',
        'name',
        'mobile',
        'email',
        'emp_id',
        'password',
        'division',
        'dept_code',
        'dept_owner',
        'demand_num',
        'email_business_owner',
        'purpose',
        'channel_type',
      ];

      for (const field of fieldsToValidate) {
        const error = getFieldError(field, values[field], context);
        if (error) {
          nextErrors[field] = error;
        }
      }

      return nextErrors;
    },
    []
  );

  const roleOptions = useMemo(
    () =>
      roles.map((role) => ({
        value: String(role.id),
        label: role.display_name ?? role.role_name,
      })),
    [roles]
  );

  const purposeSelectOptions = useMemo(() => {
    // Ensure value is always a string to match form values
    const options = purposeOptions.map((purpose) => ({
      value: String(purpose.purpose_code),
      label: `${purpose.purpose_code} - ${purpose.purpose_text}`,
    }));

    return options;
  }, [purposeOptions]);

  const channelSelectOptions = useMemo(
    () =>
      channelTypes.map((channel) => ({
        value: channel,
        label: channel,
      })),
    [channelTypes]
  );

  /**
   * Compute if the form is valid for enabling/disabling the submit button
   * This checks all required fields are filled (not empty) based on role
   */
  const isFormValid = useMemo(() => {
    const formRequired = isFormRequired(formValues.user_role_id);
    const loanOfficer = isLoanOfficerRole(formValues.user_role_id);

    // Safe trim helper to handle non-string values (e.g., numbers from API)
    const safeTrim = (val: unknown): string => String(val ?? '').trim();

    // Basic required fields for all roles
    const hasBasicFields =
      formValues.user_role_id &&
      safeTrim(formValues.name) !== '' &&
      safeTrim(formValues.mobile) !== '' &&
      safeTrim(formValues.email) !== '' &&
      safeTrim(formValues.emp_id) !== '';

    // Password required only in create mode
    const hasPassword = mode === 'edit' || (formValues.password && safeTrim(formValues.password) !== '');

    // Role-specific required fields
    const hasRoleFields = !formRequired || (
      safeTrim(formValues.division) !== '' &&
      safeTrim(formValues.dept_code) !== '' &&
      safeTrim(formValues.dept_owner) !== '' &&
      safeTrim(formValues.email_business_owner) !== ''
    );

    // Loan officer additional fields
    const hasLoanOfficerFields = !formRequired || !loanOfficer || (
      safeTrim(formValues.demand_num) !== '' &&
      formValues.purpose.length > 0 &&
      formValues.channel_type.length > 0
    );

    // No existence or whitelisting errors
    const hasNoExistenceErrors = !existenceErrors.email && !existenceErrors.mobile && !existenceErrors.emp_id;
    const hasNoWhitelistError = emailWhitelistError === null;

    // No validation errors (only check if user has interacted with fields)
    const hasNoCurrentErrors = Object.keys(errors).length === 0;

    return (
      hasBasicFields &&
      hasPassword &&
      hasRoleFields &&
      hasLoanOfficerFields &&
      hasNoExistenceErrors &&
      hasNoWhitelistError &&
      hasNoCurrentErrors
    );
  }, [formValues, mode, existenceErrors, emailWhitelistError, errors]);

  if (!isOpen) {
    return null;
  }

  // Computed role-based requirements for UI display
  const formRequired = isFormRequired(formValues.user_role_id);
  const loanOfficer = isLoanOfficerRole(formValues.user_role_id);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validation = validate(formValues, mode === 'create');
    setErrors(validation);

    // Check for existence errors and email whitelisting
    const hasExistenceErrors = existenceErrors.email || existenceErrors.mobile || existenceErrors.emp_id;
    const hasWhitelistError = emailWhitelistError !== null;

    if (Object.keys(validation).length > 0 || hasExistenceErrors || hasWhitelistError) {
      return;
    }
    await onSubmit(formValues);
  };

  const handleFinalSubmit = () => {
    const validation = validate(formValues, mode === 'create');
    setErrors(validation);

    // Check for existence errors and email whitelisting
    const hasExistenceErrors = existenceErrors.email || existenceErrors.mobile || existenceErrors.emp_id;
    const hasWhitelistError = emailWhitelistError !== null;

    if (Object.keys(validation).length > 0 || hasExistenceErrors || hasWhitelistError) {
      return;
    }
    onSubmit(formValues);
  };

  return (
    <>
      <DrawerOverlay />
      <Drawer variant="overlay" width="720px">
        <DrawerContent className="equal-ds-root flex flex-col h-full max-h-screen">
          <DrawerHeader className="border-b border-border-primary px-6 py-4">
            <h2 className="text-xl font-semibold text-text-primary">
              {mode === 'create' ? 'Create User' : 'Edit User'}
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              Fill in the details below to {mode === 'create' ? 'create' : 'update'} the user.
            </p>
          </DrawerHeader>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-gray-50">
            {isLoadingOptions ? (
              <div className="flex items-center justify-center py-20 text-text-secondary">
                <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                Loading form data...
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Role
                    </label>
                    <Dropdown>
                      <DropdownTrigger
                        className={`w-full justify-between ${
                          errors.user_role_id ? 'border-red-300' : ''
                        }`}
                      >
                        {roleOptions.find((r) => r.value === formValues.user_role_id)?.label || 'Select role'}
                      </DropdownTrigger>
                      <DropdownContent zIndex={9999}>
                        {roleOptions.map((role) => (
                          <DropdownItem
                            key={role.value}
                            onSelect={() => handleInputChange('user_role_id', role.value)}
                          >
                            {role.label}
                          </DropdownItem>
                        ))}
                      </DropdownContent>
                    </Dropdown>
                    {errors.user_role_id ? (
                      <p className="text-xs text-red-500 mt-1">{errors.user_role_id}</p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formValues.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      onBlur={(e) => validateField('name', e.target.value)}
                      placeholder="Enter Name"
                      className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.name ? <p className="text-xs text-red-500 mt-1">{errors.name}</p> : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formValues.mobile}
                      onChange={(e) => handleInputChange('mobile', e.target.value)}
                      onBlur={(e) => {
                        validateField('mobile', e.target.value);
                        checkKeyExistence('mobile', formValues.mobile);
                      }}
                      placeholder="Enter Mobile Number"
                      maxLength={10}
                      className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.mobile || existenceErrors.mobile ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.mobile ? <p className="text-xs text-red-500 mt-1">{errors.mobile}</p> : null}
                    {existenceErrors.mobile && !errors.mobile ? (
                      <p className="text-xs text-red-500 mt-1">Mobile number already exists.</p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={formValues.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onBlur={(e) => {
                        validateField('email', e.target.value);
                        if (mode === 'create') {
                          checkKeyExistence('email', formValues.email);
                        }
                      }}
                      placeholder="Enter email address"
                      disabled={mode === 'edit'}
                      className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.email || existenceErrors.email || emailWhitelistError ? 'border-red-500' : 'border-gray-300'
                      } ${mode === 'edit' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    {errors.email ? <p className="text-xs text-red-500 mt-1">{errors.email}</p> : null}
                    {emailWhitelistError && !errors.email ? (
                      <p className="text-xs text-red-500 mt-1">{emailWhitelistError}</p>
                    ) : null}
                    {existenceErrors.email && !errors.email && !emailWhitelistError ? (
                      <p className="text-xs text-red-500 mt-1">Email already exists.</p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Employee ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formValues.emp_id}
                      onChange={(e) => handleInputChange('emp_id', e.target.value)}
                      onBlur={(e) => {
                        validateField('emp_id', e.target.value);
                        checkKeyExistence('emp_id', formValues.emp_id);
                      }}
                      placeholder="Enter Employee ID"
                      className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.emp_id || existenceErrors.emp_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.emp_id ? <p className="text-xs text-red-500 mt-1">{errors.emp_id}</p> : null}
                    {existenceErrors.emp_id && !errors.emp_id ? (
                      <p className="text-xs text-red-500 mt-1">Employee ID already exists.</p>
                    ) : null}
                  </div>

                  {/* Password field - only for create mode */}
                  {mode === 'create' && (
                    <div>
                      <label className="block text-sm font-semibold text-text-secondary mb-2">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        value={formValues.password ?? ''}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        onBlur={(e) => validateField('password', e.target.value)}
                        placeholder="Enter Password"
                        className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          errors.password ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.password ? <p className="text-xs text-red-500 mt-1">{errors.password}</p> : null}
                      <p className="text-xs text-text-secondary mt-1">
                        Min 8 chars with uppercase, lowercase, number & special character.
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Business Department Code {formRequired && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formValues.dept_code ?? ''}
                      onChange={(e) => handleInputChange('dept_code', e.target.value)}
                      onBlur={(e) => validateField('dept_code', e.target.value)}
                      placeholder="Enter Business Department Code"
                      maxLength={12}
                      className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.dept_code ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.dept_code ? (
                      <p className="text-xs text-red-500 mt-1">{errors.dept_code}</p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Department/Division name {formRequired && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formValues.division ?? ''}
                      onChange={(e) => handleInputChange('division', e.target.value)}
                      onBlur={(e) => validateField('division', e.target.value)}
                      placeholder="Enter Department/Division name"
                      className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.division ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.division ? <p className="text-xs text-red-500 mt-1">{errors.division}</p> : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Business Department Owner {formRequired && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formValues.dept_owner ?? ''}
                      onChange={(e) => handleInputChange('dept_owner', e.target.value)}
                      onBlur={(e) => validateField('dept_owner', e.target.value)}
                      placeholder="Enter Business Department Owner"
                      className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.dept_owner ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.dept_owner ? <p className="text-xs text-red-500 mt-1">{errors.dept_owner}</p> : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Demand No {formRequired && loanOfficer && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formValues.demand_num ?? ''}
                      onChange={(e) => handleInputChange('demand_num', e.target.value)}
                      onBlur={(e) => validateField('demand_num', e.target.value)}
                      placeholder="Enter Demand No"
                      className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.demand_num ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.demand_num ? <p className="text-xs text-red-500 mt-1">{errors.demand_num}</p> : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Email Id of Business Owner {formRequired && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="email"
                      value={formValues.email_business_owner ?? ''}
                      onChange={(e) => handleInputChange('email_business_owner', e.target.value)}
                      onBlur={(e) => validateField('email_business_owner', e.target.value)}
                      placeholder="Enter Email Id of Business Owner"
                      className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.email_business_owner ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.email_business_owner ? <p className="text-xs text-red-500 mt-1">{errors.email_business_owner}</p> : null}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Purpose Details {formRequired && loanOfficer && <span className="text-red-500">*</span>}
                    </label>
                    <Dropdown>
                      <DropdownTrigger
                        className={`w-full justify-between ${
                          errors.purpose ? 'border-red-300' : ''
                        }`}
                      >
                        {formValues.purpose.length > 0
                          ? `${formValues.purpose.length} selected`
                          : 'Select purpose'}
                      </DropdownTrigger>
                      <DropdownContentMultiselect
                        options={purposeSelectOptions}
                        selectedValues={formValues.purpose}
                        onSelectionChange={(values) => handleMultiSelectChange('purpose', values)}
                        enableSelectAll
                        selectAllLabel="Select All"
                        enableSearch
                        searchPlaceholder="Search purpose..."
                        maxHeight="250px"
                        zIndex={10000}
                      />
                    </Dropdown>
                    {errors.purpose ? (
                      <p className="text-xs text-red-500 mt-1">{errors.purpose}</p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      Channel Types {formRequired && loanOfficer && <span className="text-red-500">*</span>}
                    </label>
                    <Dropdown>
                      <DropdownTrigger
                        className={`w-full justify-between ${
                          errors.channel_type ? 'border-red-300' : ''
                        }`}
                      >
                        {formValues.channel_type.length > 0
                          ? `${formValues.channel_type.length} selected`
                          : 'Select channel type'}
                      </DropdownTrigger>
                      <DropdownContentMultiselect
                        options={channelSelectOptions}
                        selectedValues={formValues.channel_type}
                        onSelectionChange={(values) => handleMultiSelectChange('channel_type', values)}
                        enableSelectAll
                        selectAllLabel="Select All"
                        enableSearch
                        searchPlaceholder="Search channel..."
                        maxHeight="250px"
                        zIndex={10000}
                      />
                    </Dropdown>
                    {errors.channel_type ? (
                      <p className="text-xs text-red-500 mt-1">{errors.channel_type}</p>
                    ) : null}
                  </div>
                </div>

                {formRequired ? (
                  <div className="text-sm text-text-secondary bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                    {loanOfficer
                      ? 'This role requires all business and department details including demand number, purpose, and channel type.'
                      : 'This role requires business and department details. Ensure all marked fields are completed.'}
                  </div>
                ) : null}
              </>
            )}

            <div className="h-4" />
          </form>

          <div className="border-t border-border-primary px-6 py-4 bg-background-tertiary flex items-center justify-end gap-3">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting || isLoadingOptions}
              className="bg-white text-text-primary border border-border-default hover:bg-background-tertiary"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleFinalSubmit}
              disabled={isSubmitting || isLoadingOptions || !isFormValid}
            >
              {isSubmitting
                ? mode === 'create'
                  ? 'Creating…'
                  : 'Updating…'
                : mode === 'create'
                ? 'Create User'
                : 'Update User'}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
